/**
 * The Wild Turtle — theme.js
 * Core theme functionality
 */

(() => {
  'use strict';

  /* =========================================================
     Utility helpers
  ========================================================= */
  const qs  = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const on  = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const off = (el, ev, fn) => el && el.removeEventListener(ev, fn);

  function trapFocus(el) {
    const focusable = qsa('button,a,[href],[tabindex]:not([tabindex="-1"]),input,select,textarea', el)
      .filter(f => !f.hasAttribute('disabled'));
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
      else            { if (document.activeElement === last)  { e.preventDefault(); first.focus(); } }
    }
    on(el, 'keydown', handler);
    first.focus();
    return () => off(el, 'keydown', handler);
  }

  function openOverlay() { qs('#page-overlay')?.classList.add('is-visible'); }
  function closeOverlay(){ qs('#page-overlay')?.classList.remove('is-visible'); }

  /* =========================================================
     Sticky header scroll behaviour
  ========================================================= */
  (() => {
    const header = qs('#site-header');
    if (!header) return;
    let lastY = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('scrolled', y > 20);
      lastY = y;
    }, { passive: true });
  })();

  /* =========================================================
     Mobile Navigation
  ========================================================= */
  (() => {
    const toggle  = qs('#mobile-menu-toggle');
    const nav     = qs('#mobile-nav');
    const close   = qs('#mobile-nav-close');
    const overlay = qs('#page-overlay');
    if (!toggle || !nav) return;

    let releaseTrap;

    function openNav() {
      nav.classList.add('is-open');
      nav.removeAttribute('aria-hidden');
      nav.removeAttribute('inert');
      toggle.setAttribute('aria-expanded', 'true');
      openOverlay();
      releaseTrap = trapFocus(nav);
    }

    function closeNav() {
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      nav.setAttribute('inert', '');
      toggle.setAttribute('aria-expanded', 'false');
      closeOverlay();
      releaseTrap?.();
      toggle.focus();
    }

    on(toggle, 'click', openNav);
    on(close,  'click', closeNav);
    on(overlay, 'click', closeNav);
    on(document, 'keydown', e => { if (e.key === 'Escape') closeNav(); });

    // Mobile accordion sub-menus
    qsa('[data-mobile-accordion]').forEach(btn => {
      on(btn, 'click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', !expanded);
        btn.nextElementSibling?.classList.toggle('is-open');
      });
    });
  })();

  /* =========================================================
     Desktop Navigation dropdowns
  ========================================================= */
  (() => {
    qsa('[data-dropdown]').forEach(item => {
      const btn  = qs('button', item);
      const menu = qs('.header-dropdown', item);
      if (!btn || !menu) return;

      function open()  { btn.setAttribute('aria-expanded', 'true');  menu.classList.add('is-open'); }
      function close() { btn.setAttribute('aria-expanded', 'false'); menu.classList.remove('is-open'); }

      on(item, 'mouseenter', open);
      on(item, 'mouseleave', close);
      on(btn,  'click', () => btn.getAttribute('aria-expanded') === 'true' ? close() : open());

      on(document, 'click', e => { if (!item.contains(e.target)) close(); });
      on(document, 'keydown', e => { if (e.key === 'Escape') close(); });
    });
  })();

  /* =========================================================
     Search Toggle
  ========================================================= */
  (() => {
    const btn    = qs('#search-toggle');
    const search = qs('#header-search') || qs('#predictive-search');
    if (!btn || !search) return;

    function openSearch() {
      search.removeAttribute('hidden');
      search.removeAttribute('aria-hidden');
      btn.setAttribute('aria-expanded', 'true');
      qs('input', search)?.focus();
      openOverlay();
    }

    function closeSearch() {
      search.setAttribute('hidden', '');
      search.setAttribute('aria-hidden', 'true');
      btn.setAttribute('aria-expanded', 'false');
      closeOverlay();
    }

    on(btn, 'click', () => {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      isOpen ? closeSearch() : openSearch();
    });

    on(qs('#page-overlay'), 'click', closeSearch);
    on(document, 'keydown', e => { if (e.key === 'Escape') closeSearch(); });
    on(qs('#predictive-search-close'), 'click', closeSearch);
  })();

  /* =========================================================
     Accordion (FAQ, product description)
  ========================================================= */
  (() => {
    on(document, 'click', e => {
      const btn = e.target.closest('[data-accordion-toggle]');
      if (!btn) return;
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', !expanded);
      const body = btn.nextElementSibling;
      if (body) {
        if (expanded) body.setAttribute('hidden', '');
        else          body.removeAttribute('hidden');
      }
    });
  })();

  /* =========================================================
     Filter Group Toggle (sidebar)
  ========================================================= */
  (() => {
    on(document, 'click', e => {
      const btn = e.target.closest('[data-filter-toggle]');
      if (!btn) return;
      const expanded = btn.getAttribute('aria-expanded') !== 'false';
      btn.setAttribute('aria-expanded', !expanded);
    });
  })();

  /* =========================================================
     Carousel
  ========================================================= */
  class Carousel {
    constructor(el) {
      this.el    = el;
      this.track = qs('.carousel__track', el);
      this.prev  = qs('.carousel__btn--prev', el);
      this.next  = qs('.carousel__btn--next', el);
      this.dots  = qs('.carousel__dots', el);
      this.index = 0;
      this.perView = this._getPerView();
      this._build();
    }

    _getPerView() {
      const w = window.innerWidth;
      if (w >= 1024) return 4;
      if (w >= 768)  return 3;
      if (w >= 480)  return 2;
      return 1;
    }

    _slides() { return qsa('.carousel__slide', this.track); }

    _pages() {
      return Math.ceil(this._slides().length / this._getPerView());
    }

    _build() {
      const pages = this._pages();
      if (this.dots) {
        this.dots.innerHTML = '';
        for (let i = 0; i < pages; i++) {
          const dot = document.createElement('button');
          dot.className = 'carousel__dot' + (i === 0 ? ' is-active' : '');
          dot.setAttribute('aria-label', `Go to slide group ${i + 1}`);
          dot.setAttribute('role', 'tab');
          on(dot, 'click', () => this.goTo(i));
          this.dots.appendChild(dot);
        }
      }

      on(this.prev, 'click', () => this.goTo(this.index - 1));
      on(this.next, 'click', () => this.goTo(this.index + 1));

      // Touch swipe
      let startX = 0;
      on(this.track, 'touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
      on(this.track, 'touchend', e => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) this.goTo(this.index + (diff > 0 ? 1 : -1));
      });

      window.addEventListener('resize', () => {
        const newPerView = this._getPerView();
        if (newPerView !== this.perView) {
          this.perView = newPerView;
          this.index = 0;
          this._build();
          this._update();
        }
      }, { passive: true });

      this._update();
    }

    goTo(i) {
      const pages = this._pages();
      this.index = Math.max(0, Math.min(i, pages - 1));
      this._update();
    }

    _update() {
      const perView = this._getPerView();
      const slides  = this._slides();
      const gap     = 20; // --sp-5
      const slideW  = (this.track.parentElement.offsetWidth - gap * (perView - 1)) / perView;
      const offset  = this.index * (slideW + gap) * perView;
      this.track.style.transform = `translateX(-${offset}px)`;

      // Buttons
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index >= this._pages() - 1;

      // Dots
      if (this.dots) {
        qsa('.carousel__dot', this.dots).forEach((d, i) => {
          d.classList.toggle('is-active', i === this.index);
        });
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    qsa('[data-carousel]').forEach(el => new Carousel(el));
  });

  /* =========================================================
     Impact Counter (count-up animation)
  ========================================================= */
  (() => {
    const grid = qs('[data-animate-counters]');
    if (!grid) return;

    function countUp(el, target, duration = 2000) {
      const valueEl = qs('.impact-stat__value', el);
      if (!valueEl) return;
      const start     = performance.now();
      const numTarget = parseInt(target, 10);
      const isFloat   = target.includes('.');

      function step(now) {
        const elapsed  = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased    = 1 - Math.pow(1 - progress, 3);
        const current  = Math.floor(eased * numTarget);
        valueEl.textContent = isFloat
          ? (eased * parseFloat(target)).toFixed(1)
          : current.toLocaleString();
        if (progress < 1) requestAnimationFrame(step);
        else valueEl.textContent = isFloat ? parseFloat(target).toFixed(1) : numTarget.toLocaleString();
      }
      requestAnimationFrame(step);
    }

    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        qsa('.impact-stat', grid).forEach(stat => {
          const numberEl = qs('.impact-stat__number', stat);
          const target   = numberEl?.dataset.target;
          if (target) countUp(stat, target);
        });
        obs.disconnect();
      });
    }, { threshold: 0.3 });

    obs.observe(grid);
  })();

  /* =========================================================
     Gallery (product page)
  ========================================================= */
  (() => {
    const gallery = qs('#product-gallery');
    if (!gallery) return;

    const slides = qsa('.product-gallery__slide', gallery);
    const thumbs  = qsa('.product-gallery__thumb', gallery);
    const prev    = qs('#gallery-prev', gallery);
    const next    = qs('#gallery-next', gallery);
    let current   = 0;

    function show(index) {
      slides.forEach((s, i) => {
        s.classList.toggle('is-active', i === index);
        i === index ? s.removeAttribute('hidden') : s.setAttribute('hidden', '');
      });
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      current = index;
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
    }

    thumbs.forEach((t, i) => on(t, 'click', () => show(i)));
    on(prev, 'click', () => show(Math.max(0, current - 1)));
    on(next, 'click', () => show(Math.min(slides.length - 1, current + 1)));

    show(0);
  })();

  /* =========================================================
     Quantity controls
  ========================================================= */
  on(document, 'click', e => {
    const btn = e.target.closest('.qty-control__btn');
    if (!btn) return;
    const wrap  = btn.closest('.qty-control');
    const input = qs('.qty-control__value', wrap);
    if (!input) return;
    const val = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 0;
    const action = btn.dataset.action;
    if (action === 'increase') {
      input.value = val + 1;
    } else if (action === 'decrease' && val > min) {
      input.value = Math.max(min, val - 1);
    }
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* =========================================================
     Cart notification toast
  ========================================================= */
  function showCartNotification(message = 'Added to cart') {
    const notif = qs('#cart-notification');
    if (!notif) return;
    qs('.cart-notification__text', notif).textContent = message;
    notif.removeAttribute('hidden');
    setTimeout(() => notif.setAttribute('hidden', ''), 3000);
  }

  on(qs('#cart-notification'), 'click', e => {
    if (e.target.closest('.cart-notification__close')) {
      qs('#cart-notification')?.setAttribute('hidden', '');
    }
  });

  window.showCartNotification = showCartNotification;

  /* =========================================================
     Update cart count in header
  ========================================================= */
  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.dataset.count = count;
      if (count === 0) el.setAttribute('data-count', '0');
      else             el.removeAttribute('data-count');
    });
  }

  window.updateCartCount = updateCartCount;

  /* =========================================================
     Grid toggle (collection page)
  ========================================================= */
  (() => {
    const btns = qsa('.grid-toggle__btn');
    const grid = qs('#product-grid');
    if (!btns.length || !grid) return;

    btns.forEach(btn => {
      on(btn, 'click', () => {
        btns.forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        grid.dataset.columns = btn.dataset.grid;
      });
    });
  })();

  /* =========================================================
     Sticky ATC bar (mobile, product page)
  ========================================================= */
  (() => {
    const atcBtn   = qs('#product-atc-btn');
    const stickyEl = qs('.sticky-atc');
    if (!atcBtn || !stickyEl) return;

    const obs = new IntersectionObserver(entries => {
      stickyEl.classList.toggle('is-visible', !entries[0].isIntersecting);
    });
    obs.observe(atcBtn);
  })();

  /* =========================================================
     FAQ / category smooth scroll
  ========================================================= */
  (() => {
    qsa('.faq-categories__link').forEach(link => {
      on(link, 'click', e => {
        e.preventDefault();
        const id  = link.getAttribute('href').slice(1);
        const target = document.getElementById(id);
        if (!target) return;
        qsa('.faq-categories__link').forEach(l => l.classList.remove('is-active'));
        link.classList.add('is-active');
        const top = target.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  })();

  /* =========================================================
     Wishlist (UI only — integrate with favourite app)
  ========================================================= */
  on(document, 'click', e => {
    const btn = e.target.closest('.product-card__wishlist, .coll-card__wishlist');
    if (!btn) return;
    btn.classList.toggle('is-active');
    btn.setAttribute('aria-label', btn.classList.contains('is-active') ? 'Remove from wishlist' : 'Add to wishlist');
  });

  /* =========================================================
     DOMContentLoaded init
  ========================================================= */
  document.addEventListener('DOMContentLoaded', () => {
    // Variant selection on product page managed by product-form.js
    // Cart drawer managed by cart-drawer.js
    // Filters managed by filters.js
    // Search managed by predictive-search.js
  });

})();
